import React, { useRef, useEffect, useState } from 'react'
import { Typography, useMediaQuery } from '@material-ui/core'
import { useRouter } from 'next/router'
import { MenuItem, SubMenuColumn } from '../Header'
import Link from 'next/link'
import classes from './DesktopHeader.styles'
import { cx } from 'linaria'
import commonStyling from '../../../styles/commonStyling'
import { createPortal } from 'react-dom'
import theme from '../../../styles/theme'

export interface DesktopSubHeaderProps {
  subMenuItems?: MenuItem[]
  columns?: SubMenuColumn[]
  mainMenuTitle?: string
   headerContainerRef?:React.RefObject<HTMLDivElement>;
}

export default function DesktopSubHeader({
  subMenuItems,
  columns = [],
  headerContainerRef
}: DesktopSubHeaderProps) {
  const router = useRouter()
  const currentPath = router.asPath
  const menuRef = useRef<HTMLDivElement>(null)
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)
  const isMedium = useMediaQuery(theme.breakpoints.down('md'))
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({
    position: 'absolute',
    top: 58,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10,
  })

  useEffect(() => {
    if (!menuRef.current) return
    if (!headerContainerRef?.current) return
    if (columns.length >= 3) {
      let parent = menuRef.current.parentElement
      let level = 1
      while (level < 3 && parent?.parentElement) {
        parent = parent.parentElement
        level++
      }
      setPortalContainer(parent || null)
      setMenuStyle((style) => ({
        ...style,
        top: isMedium ? 91 : 85, 
        maxWidth:headerContainerRef?.current?.clientWidth,
        minWidth:headerContainerRef?.current?.clientWidth
      }))
    } else {
      setPortalContainer(null)
      setMenuStyle((style) => ({
        ...style,
        top: 58,
      }))
    }
  }, [columns.length, subMenuItems?.length, isMedium])

  useEffect(() => {
    if (!menuRef.current) return

    const rect = menuRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    let styleUpdate: React.CSSProperties = {}
    if(columns.length < 3){

      if (rect.right > viewportWidth) {
        styleUpdate = { right: 0, left: 'auto', transform: 'none' }
      } else if (rect.left < 0) {
        styleUpdate = { left: 0, right: 'auto', transform: 'none' }
      } else {
        styleUpdate = { left: '50%', right: 'auto', transform: 'translateX(-50%)' }
      }
    }

    setMenuStyle((style) => ({
      ...style,
      ...styleUpdate,
    }))
  }, [columns.length, subMenuItems?.length])

  const menuContent = columns.length > 0 ? (
  <div className={classes.subMenuNew} ref={menuRef} style={menuStyle}>
    {[...columns]
      .sort((a, b) => a.order_in_the_queue - b.order_in_the_queue)
      .map((col, idx) => (
        <ul key={idx}>
          <li>
            <Typography
              color="primary"
              style={{ fontWeight: 600, marginBottom: col.title ? 16 : 0 }}
              variant="h5"
            >
              {col.title}
            </Typography>
          </li>
          {col.sub_menu_items.map((item, i) => {
            const isCurrentPath = item.url === currentPath
            const activeItemClass = cx(isCurrentPath && classes.activeItem)
            if (item.modal) {
              return (
                <li key={i}>
                  <a
                    className={activeItemClass}
                    href={item.url ?? ''}
                    onClick={(e) => {
                      e.stopPropagation()
                      router.push(`${router.asPath}${item.url}`, undefined, { shallow: true })
                    }}
                  >
                    <Typography variant="body2">{item.title}</Typography>
                  </a>
                </li>
              )
            }
            return (
              <li key={i}>
                <Link href={item.url ?? ''}>
                  <a className={activeItemClass}>
                    <Typography
                      variant="body2"
                      data-text={item.title}
                      className={commonStyling.differentFontWeightOnHover}
                    >
                      {item.title}
                    </Typography>
                  </a>
                </Link>
              </li>
            )
          })}
        </ul>
      ))}
  </div>
) : subMenuItems?.length ? (
  <ul className={classes.submenu}>
    {subMenuItems.map((item, i) => {
      const isCurrentPath = item.url === currentPath
      const activeItemClass = cx(isCurrentPath && classes.activeItem)

      if (item.modal) {
        return (
          <li key={i}>
            <a
              className={activeItemClass}
              href={item.url ?? ''}
              onClick={(e) => {
                e.stopPropagation()
                router.push(`${router.asPath}${item.url}`, undefined, { shallow: true })
              }}
            >
              <Typography variant="body2">{item.title}</Typography>
            </a>
          </li>
        )
      }

      return (
        <li key={i}>
          <Link href={item.url ?? ''}>
            <a className={activeItemClass}>
              <Typography
                variant="body2"
                data-text={item.title}
                className={commonStyling.differentFontWeightOnHover}
              >
                {item.title}
              </Typography>
            </a>
          </Link>
        </li>
      )
    })}
  </ul>
) : null

  if (portalContainer) {
    return createPortal(menuContent, portalContainer)
  }

  return menuContent
}
